import React, { useState } from "react";
import { Type, Loader2, Eraser, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const OverlayEditor = ({ contentId, onUpdated, testId = "overlay", className = "" }) => {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [ov, setOv] = useState({ brand_name: "", caption: "", info: "", show_logo: false });
  const [busy, setBusy] = useState(false);

  const openEditor = async () => {
    setOpen(true); setInfo(null);
    try {
      const { data } = await api.get(`/content/${contentId}/overlay`);
      setInfo(data); setOv({ ...data.ov, show_logo: data.ov.show_logo ?? false });
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); setOpen(false); }
  };

  const save = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/content/${contentId}/overlay`, next);
      onUpdated?.(data);
      toast.success("تم تحديث النصوص");
      setOpen(false);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const f = (k) => (e) => setOv({ ...ov, [k]: e.target.value });

  return (
    <>
      <Button type="button" variant="outline" onClick={openEditor} className={`gap-2 border-border ${className}`} data-testid={`${testId}-btn`}>
        <Type className="w-4 h-4" /> النصوص والشعار
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover border-border max-w-md" dir="rtl" data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle>تعديل النصوص على التصميم</DialogTitle>
            <DialogDescription>أبقِ النصوص أو عدّلها أو أزلها — بدون إعادة توليد وبدون تكلفة.</DialogDescription>
          </DialogHeader>
          {!info ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          : !info.editable ? <p className="text-sm text-muted-foreground py-4" data-testid={`${testId}-not-editable`}>هذا المحتوى أُنشئ قبل ميزة التعديل — أعد توليده لتتمكن من تعديل نصوصه.</p>
          : (
            <div className="space-y-3">
              <div><Label className="mb-1 block text-xs">اسم العلامة</Label><Input value={ov.brand_name} onChange={f("brand_name")} className="bg-secondary border-border" data-testid={`${testId}-brand`} /></div>
              <div><Label className="mb-1 block text-xs">العنوان</Label><Input value={ov.caption} onChange={f("caption")} className="bg-secondary border-border" data-testid={`${testId}-caption`} /></div>
              <div><Label className="mb-1 block text-xs">معلومات إضافية</Label><Input value={ov.info} onChange={f("info")} className="bg-secondary border-border" data-testid={`${testId}-info`} /></div>
              {ov.has_logo && (
                <div className="flex items-center justify-between rounded-xl bg-secondary px-3 py-2">
                  <span className="text-sm">إظهار الشعار</span>
                  <Switch checked={!!ov.show_logo} onCheckedChange={(v) => setOv({ ...ov, show_logo: v })} data-testid={`${testId}-logo-switch`} />
                </div>
              )}
              {info.type === "video" && <p className="text-[11px] text-muted-foreground">إعادة تركيب الفيديو تستغرق ثوانٍ.</p>}
              <div className="grid grid-cols-2 gap-2 pt-1">
                <Button onClick={() => save(ov)} disabled={busy} className="gap-2" data-testid={`${testId}-save`}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} حفظ
                </Button>
                <Button variant="outline" onClick={() => save({ brand_name: "", caption: "", info: "", show_logo: ov.show_logo })} disabled={busy}
                  className="gap-2 border-border" data-testid={`${testId}-clear`}>
                  <Eraser className="w-4 h-4" /> إزالة النصوص
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
